import { RegisteredComponent } from "@builder.io/sdk-react";
import { DLD_FIELD, DLT_FIELD } from "../commonFields";
import { designTokens } from "../designTokens";

export const AccordionBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Accordion",
  image:
    "https://icons.veryicon.com/png/o/miscellaneous/ty-icon/layout-accordion-separated.png",
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderContext: true,
    builderComponents: true,
    builderLinkComponent: true,
  },
  canHaveChildren: true,
  inputs: [
    {
      name: "columnsCount",
      type: "number",
      defaultValue: 2,
      friendlyName: "Number of columns",
      helperText: "Number of columns to display the accordion items in.",
      localized: false,
    },
    {
      name: "multipleOpen",
      type: "boolean",
      defaultValue: false,
      friendlyName: "Allow multiple open sections",
      helperText:
        "If enabled, multiple accordion sections can be open at the same time.",
      localized: false,
    },
    {
      name: "preopenedSectionIndex",
      type: "number",
      defaultValue: 0,
      friendlyName: "Pre-opened section index",
      helperText:
        "Index (starts from 1) of the section that should be open when the page loads. Ignored if value is 0 or more than the number of sections.",
      localized: false,
    },
    {
      name: "tabStyle",
      type: "uiStyle",
      localized: false,
      defaultValue: {
        color: designTokens.colors["Tab Link"],
      },
    },
    {
      name: "tabHoverStyle",
      type: "uiStyle",
      localized: false,
      defaultValue: {
        textDecoration: "underline",
        backgroundColor: designTokens.colors["Background Highlight"],
        color: designTokens.colors["Tab Link Hover"],
      },
    },
    {
      name: "tabActiveStyle",
      type: "uiStyle",
      localized: false,
      defaultValue: {
        color: designTokens.colors["Tab Link Pressed"],
      },
    },
    {
      name: "tabFocusStyle",
      type: "uiStyle",
      localized: false,
      defaultValue: {
        backgroundColor: designTokens.colors["Background Highlight"],
        color: designTokens.colors["Tab Link Hover"],
      },
    },
    {
      name: "items",
      type: "list",
      required: true,
      subFields: [
        {
          name: "labelBlock",
          type: "uiBlocks",
          localized: true,
          defaultValue: [],
        },
        DLT_FIELD,
        DLD_FIELD,
        {
          name: "blocks",
          type: "uiBlocks",
          localized: true,
          defaultValue: [],
        },
      ],
      defaultValue: [
        {
          labelBlock: [
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Label",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Tab 1",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      fontWeight: "var(--font-weight-bold, 700)",
                      fontSize: "var(--text-body-l, 1.25rem)",
                    },
                    medium: {
                      fontSize: "var(--text-body-m, 1.25rem)",
                    },
                    xsmall: {
                      fontSize: "var(--text-body-s, 1.25rem)",
                    },
                  },
                },
              ],
            },
          ],
          dlt: "DLT Tab 1",
          dld: "DLD Tab 1",
          blocks: [
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Content",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Tab 1 Content",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      color: designTokens.colors["Body Primary"],
                    },
                  },
                },
              ],
            },
          ],
        },
        {
          labelBlock: [
            {
              "@type": "@builder.io/sdk:Element",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  layerName: "Label",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Tab 2",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      fontWeight: "var(--font-weight-bold, 700)",
                      fontSize: "var(--text-body-l, 1.25rem)",
                    },
                    medium: {
                      fontSize: "var(--text-body-m, 1.25rem)",
                    },
                    xsmall: {
                      fontSize: "var(--text-body-s, 1.25rem)",
                    },
                  },
                },
              ],
            },
          ],
          dlt: "DLT Tab 2",
          dld: "DLD Tab 2",
          blocks: [
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Content",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Tab 2 Content",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      color: designTokens.colors["Body Primary"],
                    },
                  },
                },
              ],
            },
          ],
        },
        {
          labelBlock: [
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Label",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Tab 3",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      fontWeight: "var(--font-weight-bold, 700)",
                      fontSize: "var(--text-body-l, 1.25rem)",
                    },
                    medium: {
                      fontSize: "var(--text-body-m, 1.25rem)",
                    },
                    xsmall: {
                      fontSize: "var(--text-body-s, 1.25rem)",
                    },
                  },
                },
              ],
            },
          ],
          dlt: "DLT Tab 3",
          dld: "DLD Tab 3",
          blocks: [
            {
              "@type": "@builder.io/sdk:Element",
              layerName: "Content",
              children: [
                {
                  "@type": "@builder.io/sdk:Element",
                  component: {
                    name: "Text",
                    options: {
                      text: {
                        "@type": "@builder.io/core:LocalizedValue",
                        Default: "Tab 3 Content",
                      },
                    },
                  },
                  meta: {
                    localizedTextInputs: ["text"],
                    "transformed.text": "localized",
                  },
                  responsiveStyles: {
                    large: {
                      color: designTokens.colors["Body Primary"],
                    },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  defaults: {
    responsiveStyles: {
      large: {
        display: "flex",
        flexDirection: "row",
        width: "100%",
        gap: "var(--spacing-4)",
      },
      small: { display: "flex", flexDirection: "column", width: "100%" },
    },
  },
};
